// src/pages/Payments.js

import React, { useEffect, useState } from "react";

import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  Wallet,
  ArrowDownCircle,
  ArrowUpCircle,
  CreditCard,
  Banknote,
  Send,
  Clock,
  X,
  Link2,
  CheckCircle,
  AlertCircle,
  History,
  Briefcase,
  Award,
  DollarSign,
  Info,
} from "lucide-react";

const Payments = () => {
  const [wallet, setWallet] = useState({
    walletBalance: 0,
    walletHistory: [],
    jobsCompleted: 0,
    totalEarnings: 0,
  });
  const [withdrawLogs, setWithdrawLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [withdrawMethod, setWithdrawMethod] = useState("");
  const [withdrawAmount, setWithdrawAmount] = useState("");
  const [message, setMessage] = useState("");
  const [stripeStatus, setStripeStatus] = useState(null);
  const [connectingStripe, setConnectingStripe] = useState(false);
  const navigate = useNavigate();
  const workerId = localStorage.getItem("workerId");
  const API_WRK =
    process.env.REACT_APP_API_WRK || "https://api.bookyourneed.com/api/worker";
  const API_BASE =
    process.env.REACT_APP_API_BASE || "https://api.bookyourneed.com";

  // ===========================================================
  // 🧾 Fetch Wallet + Stripe + Withdrawal Logs
  // ===========================================================
  useEffect(() => {
    if (!workerId) return;

    const fetchWallet = async () => {
      try {
        const res = await axios.get(`${API_WRK}/wallet/${workerId}`);
        setWallet(res.data);
      } catch (err) {
        console.error("❌ Failed to load wallet:", err);
      } finally {
        setLoading(false);
      }
    };

    const fetchStripeStatus = async () => {
      try {
        const res = await axios.get(
          `${API_BASE}/api/stripe/account-status/${workerId}`
        );
        setStripeStatus(res.data || { payoutsEnabled: false });
      } catch (err) {
        setStripeStatus({ payoutsEnabled: false });
      }
    };

    const fetchWithdrawLogs = async () => {
      try {
        const res = await axios.get(`${API_WRK}/withdrawal-logs`);
        const logs = res.data.filter(
          (log) => log.email === wallet.workerEmail
        );
        setWithdrawLogs(logs);
      } catch (err) {
        console.error("❌ Withdrawal logs error:", err);
      }
    };

    fetchWallet();
    fetchStripeStatus();
    fetchWithdrawLogs();
  }, [workerId, wallet.workerEmail]);

  // ===========================================================
  // 💸 Handle Withdraw Request
  // ===========================================================
  const handleWithdraw = async () => {
  // 🔐 BLOCK withdrawal if Stripe not connected
  if (!stripeStatus?.payoutsEnabled) {
    setMessage("Please connect and complete your Stripe payout setup first.");
    return;
  }

  if (!withdrawMethod || !withdrawAmount) {
    setMessage("Please choose a payout method and amount.");
    return;
  }

  if (parseFloat(withdrawAmount) > wallet.walletBalance) {
    setMessage("Amount exceeds available balance.");
    return;
  }

  try {
    await axios.post(`${API_WRK}/request-withdrawal`, {
      workerId,
      amount: parseFloat(withdrawAmount),
      method: withdrawMethod,
    });

    setMessage("✅ Withdrawal request sent.");
    setWithdrawAmount("");
    setWithdrawMethod("");
    setShowWithdrawModal(false);
  } catch (err) {
    console.error("❌ Withdrawal request failed:", err);
    setMessage("❌ Withdrawal request failed. Please try again.");
  }
};



  // ===========================================================
  const handleConnectStripe = async () => {
    try {
      setConnectingStripe(true);
      const res = await axios.post(
        `${API_BASE}/api/stripe/create-connect-account`,
        { workerId, email: wallet.workerEmail }
      );
      window.location.href = res.data.url;
    } catch (err) {
      console.error("❌ Stripe connect error:", err);
      setMessage("❌ Failed to start Stripe onboarding.");
    } finally {
      setConnectingStripe(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Clock className="animate-spin text-blue-500 mr-2" size={28} />
        <p className="text-gray-600 text-lg">Loading wallet...</p>
      </div>
    );
  }

  // ===========================================================
  // 💰 Stripe Fee & Final Amount
  // ===========================================================
  const stripeFee = withdrawMethod === "Stripe Instant Payout" ? 2 : 0;
  const finalAmount =
    withdrawAmount && !isNaN(withdrawAmount)
      ? Math.max(parseFloat(withdrawAmount) - stripeFee, 0)
      : 0;

  // ===========================================================
  // 🧾 Earnings Split (Jobs + Rides)
  // ===========================================================
  const jobEarnings = wallet.walletHistory
    .filter((h) => h.notes?.toLowerCase().includes("job"))
    .reduce((sum, h) => sum + (h.amount || 0), 0);

  const rideEarnings = wallet.walletHistory
    .filter((h) => h.notes?.toLowerCase().includes("ride"))
    .reduce((sum, h) => sum + (h.amount || 0), 0);

  // ===========================================================
  // 🧭 MAIN UI
  // ===========================================================
  return (
    <div className="max-w-5xl mx-auto p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold text-blue-700 flex items-center gap-2">
          <Wallet size={30} className="text-blue-600" /> My Wallet
        </h1>
        <button
          onClick={() => setShowWithdrawModal(true)}
          className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2.5 rounded-lg font-semibold shadow transition"
        >
          Withdraw Funds
        </button>
      </div>

      {/* Wallet Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="bg-white shadow-sm border border-gray-100 rounded-xl p-5 flex items-center gap-4">
          <Briefcase className="text-blue-600" size={28} />
          <div>
            <p className="text-sm text-gray-500">Jobs Completed</p>
            <p className="text-2xl font-bold text-gray-800">
              {wallet.jobsCompleted || 0}
            </p>
          </div>
        </div>

        <div className="bg-white shadow-sm border border-gray-100 rounded-xl p-5 flex items-center gap-4">
          <DollarSign className="text-green-600" size={28} />
          <div>
            <p className="text-sm text-gray-500">Total Earnings</p>
            <p className="text-2xl font-bold text-gray-800">
              ${wallet.totalEarnings?.toFixed(2) || "0.00"}
            </p>
          </div>
        </div>

        <div className="bg-white shadow-sm border border-gray-100 rounded-xl p-5 flex items-center gap-4">
          <Award className="text-yellow-500" size={28} />
          <div>
            <p className="text-sm text-gray-500">Commission Tier</p>
            <p className="text-2xl font-bold text-gray-800">4.45%</p>
          </div>
        </div>
      </div>

      {/* Earnings Breakdown */}
      <div className="bg-white shadow-sm border border-gray-100 rounded-xl p-5 mb-8">
        <h2 className="text-lg font-semibold text-gray-800 mb-3">
          Earnings Breakdown
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-gray-700">
          <div>
            <p className="text-sm text-gray-500">Job Earnings</p>
            <p className="text-xl font-bold text-green-600">
              ${jobEarnings.toFixed(2)}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Ride Earnings</p>
            <p className="text-xl font-bold text-blue-600">
              ${rideEarnings.toFixed(2)}
            </p>
          </div>
        </div>
      </div>
      {/* Stripe Connection */}
<div className="bg-white shadow-sm border border-gray-100 rounded-xl p-5 mb-8">
  <div className="flex items-center justify-between">
    <div>
      <h2 className="text-lg font-semibold text-gray-800 mb-1">
        Stripe Payout Account
      </h2>

      {stripeStatus?.payoutsEnabled ? (
        <p className="text-green-600 flex items-center gap-1">
          <CheckCircle size={18} /> Connected – Payouts Enabled
        </p>
      ) : (
        <p className="text-yellow-600 flex items-center gap-1">
          <AlertCircle size={18} /> Not connected yet
        </p>
      )}

      {/* 👇 EDUCATION LINK */}
      <button
        onClick={() => navigate("/worker/stripe-guide")}
        className="mt-2 text-sm text-blue-600 hover:underline flex items-center gap-1"
      >
        <Info size={14} />
        How Stripe payouts work
      </button>
    </div>

    <button
      onClick={handleConnectStripe}
      disabled={connectingStripe}
      className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium shadow"
    >
      <Link2 size={18} />
      {connectingStripe ? "Connecting..." : "Connect Stripe"}
    </button>
  </div>
</div>

      {/* Wallet Balance */}
      <div className="bg-gradient-to-br from-blue-600 to-blue-400 text-white rounded-2xl shadow-lg p-6 mb-8">
        <h2 className="text-lg opacity-90 mb-1">Current Balance</h2>
        <p className="text-4xl font-extrabold tracking-wide">
          ${wallet.walletBalance?.toFixed(2) || "0.00"}
        </p>
        <p className="text-sm opacity-80 mt-2">Available for withdrawal</p>
      </div>

      {/* Transaction History */}
      <div className="bg-white shadow-md rounded-xl p-6 border border-gray-100 mb-8">
        <h2 className="text-2xl font-semibold text-gray-800 mb-4 flex gap-2 items-center">
          <History size={22} className="text-blue-600" /> Transaction History
        </h2>
        {wallet.walletHistory.length === 0 ? (
          <p className="text-gray-500 text-center py-6">No transactions yet.</p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {wallet.walletHistory
              .slice()
              .reverse()
              .map((entry, i) => (
                <li
                  key={i}
                  className="py-4 flex items-center justify-between hover:bg-gray-50 px-2 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    {entry.type === "credit" ? (
                      <ArrowDownCircle className="text-green-500" size={22} />
                    ) : (
                      <ArrowUpCircle className="text-red-500" size={22} />
                    )}
                    <div>
                      <p className="text-gray-900 font-medium">
                        {entry.type === "credit"
                          ? entry.notes?.toLowerCase().includes("ride")
                            ? "Ride Payout Received"
                            : "Job Payment Received"
                          : "Withdrawal Sent"}
                      </p>
                      <p className="text-sm text-gray-500">
                        {new Date(entry.date).toLocaleString()}
                      </p>
                      {entry.notes && (
                        <p className="text-xs text-gray-400 italic">
                          {entry.notes}
                        </p>
                      )}
                    </div>
                  </div>
                  <p
                    className={`text-lg font-bold ${
                      entry.type === "credit"
                        ? "text-green-600"
                        : "text-red-600"
                    }`}
                  >
                    {entry.type === "credit" ? "+" : "-"}$
                    {entry.amount?.toFixed(2)}
                  </p>
                </li>
              ))}
          </ul>
        )}
      </div>

      {/* Withdraw Modal */}
      {showWithdrawModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-lg relative">
            <button
              onClick={() => setShowWithdrawModal(false)}
              className="absolute top-3 right-3 text-gray-500 hover:text-gray-800"
            >
              <X size={20} />
            </button>

            <h2 className="text-2xl font-bold mb-4 text-blue-700">
              Withdraw Funds
            </h2>

            <label className="block mb-2 text-gray-700 font-medium">
              Select Payout Method:
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
             {["Stripe Payout"].map((method) => (
  <button
    key={method}
    onClick={() => setWithdrawMethod(method)}
    className={`border rounded-lg p-3 flex flex-col items-center gap-2 transition ${
      withdrawMethod === method
        ? "border-blue-600 bg-blue-50"
        : "border-gray-300 hover:border-blue-400"
    }`}
  >
    <CreditCard className="text-blue-600" size={22} />
    <span className="text-sm font-medium text-gray-700 text-center">
      Stripe
    </span>
  </button>
))}



            </div>

            <label className="block mb-2 text-gray-700 font-medium">
              Enter Amount:
            </label>
            <input
              type="number"
              placeholder="e.g. 50.00"
              value={withdrawAmount}
              onChange={(e) => setWithdrawAmount(e.target.value)}
              className="w-full border border-gray-300 rounded-lg p-2 mb-4 focus:ring-2 focus:ring-blue-500 outline-none"
            />

            {withdrawMethod === "Stripe Payout" && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 flex items-start gap-2 text-sm text-blue-800">
                <Info size={18} className="mt-0.5" />
                <div>
                  <p>
                    A <strong>$2 instant payout fee</strong> will be applied.
                  </p>
                  <p className="text-xs mt-1 opacity-80">
                    You’ll receive{" "}
                    <strong>${finalAmount.toFixed(2)}</strong> after fees.
                  </p>
                </div>
              </div>
            )}

            {message && (
              <p className="text-center text-sm text-gray-600 mb-2">{message}</p>
            )}

            <button
              onClick={handleWithdraw}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2.5 rounded-lg font-semibold transition"
            >
              Confirm Withdrawal
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Payments;
